import { ConflictReason } from "./ConflictReason";
import { ConflictResolutionAction } from "./ConflictResolutionAction";
import { MigrationType } from "./Statistics/MigrationType";

/** The five file families that own conflict-bearing files. Excludes the empty-folder migration types. */
export const CONFLICT_FILE_FAMILIES: MigrationType[] = [
  MigrationType.CasesMigration,
  MigrationType.IntakesMigration,
  MigrationType.ProvidersMigration,
  MigrationType.TemplatesMigration,
  MigrationType.FirmDocumentsMigration,
];

/** Unresolved-conflict aggregates for one reason within one family. File counts are the headline figure. */
export interface OrgConflictReasonSummary {
  family: MigrationType;
  conflictReason: ConflictReason;
  fileCount: number;
  /** Distinct parent entities holding at least one unresolved file of this reason. */
  entityCount: number;
  totalMegabytes: number;
  /** Of fileCount, those in a co-authorable Office format — the only real clobber risk for a replace. */
  officeFileCount: number;
}

/**
 * Totals over a set of reasons. `entityCount` is a distinct count across the whole subset computed server-side,
 * never a sum of the per-reason entity counts — one case can hold several reasons.
 */
export interface OrgConflictAggregate {
  fileCount: number;
  entityCount: number;
  totalMegabytes: number;
  officeFileCount: number;
}

export interface OrgConflictResolutionPreflight {
  reasons: OrgConflictReasonSummary[];
  selected: OrgConflictAggregate;
  residual: OrgConflictAggregate;
  /** Must be echoed back on submit; bounds the resolve to the set that was counted here. */
  preflightTakenAt: string;
  /** Files the server commits per atomic step, so progress can be expressed in the units it works in. */
  chunkSize: number;
}

export interface OrgConflictResolutionOutcome {
  family: MigrationType;
  conflictReason: ConflictReason;
  action: ConflictResolutionAction;
  resolvedFileCount: number;
  queuedMegabytes: number;
}

export interface OrgConflictResolutionResult {
  outcomes: OrgConflictResolutionOutcome[];
  leftUnresolved: OrgConflictReasonSummary[];
  totalResolvedFileCount: number;
  totalQueuedMegabytes: number;
  /** The batch stamp; with the performer it identifies every file row this operation touched. */
  resolvedOn: string;
}

/**
 * One committed batch, reported while the resolve is still running. Every figure describes work already durable on
 * the server — a batch that fails is never reported — so these counts can be shown as fact rather than an estimate.
 */
export interface OrgConflictResolutionProgress {
  family: MigrationType;
  conflictReason: ConflictReason;
  action: ConflictResolutionAction;
  /** Files this batch alone committed. */
  batchFileCount: number;
  /** Files committed so far across every family and reason the operation has reached. */
  resolvedFileCount: number;
  queuedMegabytes: number;
}

/**
 * A reason omitted from `reasonActions` is left completely untouched — no status change, no resolution type, no
 * audit entry, no re-queue. "Leave unresolved" is the absence of a key rather than a sentinel action, so the
 * server can never action a reason the operator did not choose.
 */
export interface ResolveOrgConflictsRequest {
  reasonActions: Partial<Record<ConflictReason, ConflictResolutionAction>>;
  families?: MigrationType[];
  preflightTakenAt: string;
  expectedFileCounts: Partial<Record<ConflictReason, number>>;
}

/**
 * One file touched by a bulk conflict resolution, for the audit drill-down. These come from the immutable
 * audit-log snapshot taken at the time of the event, not from the file's current conflict state — a later
 * conflict on the same file resets those columns and would otherwise erase the trail.
 */
export interface OrgConflictResolvedFile {
  fileId: string;
  migrationEntityId: string;
  family: MigrationType;
  filename: string;
  conflictReason: ConflictReason | null;
  conflictResolutionType: ConflictResolutionAction | null;
}
